'use client';

import { getCookie, setCookie } from 'cookies-next';
import { useEffect, useRef } from 'react';

import { targetEnvCookieName } from '@/constants/variables';
import type { WebAppEnvironment } from '@/types/api';
import {
  detectTargetFromWindow,
  isValidTargetEnvironment,
} from '@/utils/target-detection';

type TargetEnvInitializerProps = {
  /**
   * Server-detected target environment passed from the root layout.
   * This is used as the initial value if no cookie exists.
   */
  serverDetectedTarget: WebAppEnvironment;
};

/**
 * Client component that initializes the target environment on first load.
 *
 * This component:
 * 1. Checks if a valid target cookie already exists
 * 2. If not, detects target from URL (hostname or query param)
 * 3. Falls back to server-detected target if client detection fails
 * 4. Sets the target in cookies for consistent access
 *
 * This ensures the target is available throughout the app immediately
 * after the first page load, without requiring a sign-in flow.
 */
export function TargetEnvInitializer({ serverDetectedTarget }: TargetEnvInitializerProps) {
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only run once per mount
    if (hasInitialized.current === true) {
      return;
    }

    hasInitialized.current = true;

    // Check if cookie already exists and is valid
    const existingCookie = getCookie(targetEnvCookieName);
    if (isValidTargetEnvironment(existingCookie as string | undefined)) {
      // Cookie is already set and valid, no action needed
      return;
    }

    // Try to detect from client-side URL
    const clientDetected = detectTargetFromWindow();

    // Use client detection if available, otherwise use server-detected value
    const targetToSet = clientDetected ?? serverDetectedTarget;

    // Set the cookie
    setCookie(targetEnvCookieName, targetToSet, {
      maxAge: 60 * 60 * 24 * 30, // 30 days
      path: '/',
      sameSite: 'lax',
    });
  }, [serverDetectedTarget]);

  // This component doesn't render anything
  return null;
}
